import type {ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import {type AvailableCommand, useAvailableCommands} from '@github-ui/copilot-chat/hooks/use-available-commands'
import {extractChatCommandType} from '@github-ui/copilot-chat/utils/chat-commands-helpers'
import {getThreadStaticSuggestions} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {CopilotChatCommandType, GeneratedSuggestion} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useChatStateValues} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  BookIcon,
  ChecklistIcon,
  CommentDiscussionIcon,
  FileDiffIcon,
  GitCommitIcon,
  GitPullRequestIcon,
  HistoryIcon,
  InfoIcon,
  LightBulbIcon,
  LogIcon,
  NoteIcon,
  QuestionIcon,
  RocketIcon,
  SearchIcon,
  ShieldLockIcon,
  TelescopeIcon,
  ToolsIcon,
} from '@primer/octicons-react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {CommandPill} from './CommandPill'
import styles from './CommandStarters.module.css'
import {CommandSuggestions} from './CommandSuggestions'

type SuggestionPillsProps = {
  chatInputRef: React.RefObject<ChatInputRef | null>
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  onChatCommandChange?: (command: CopilotChatCommandType | null) => void
  chatCommand?: CopilotChatCommandType | null
  containerRef?: React.RefObject<HTMLElement | null>
}

const CREATE_ISSUE_KEY = 'create-issue'

export function SuggestionPills({
  chatInputRef,
  textAreaRef,
  onChatCommandChange,
  chatCommand,
  containerRef,
}: SuggestionPillsProps) {
  const {suggestions, currentReferences, currentRepository, context} = useChatStateValues(
    'suggestions',
    'currentReferences',
    'currentRepository',
    'context',
  )
  const manager = useChatManager()
  const {availableCommands, availableCommandKeys} = useAvailableCommands({enabled: true})
  const shownSuggestionsKeyRef = useRef<string | null>(null)

  // Compute suggestions locally from references when state suggestions are not populated.
  // When contextualChatSuggestionsUpdated is on, the reducer blocks SUGGESTIONS_GENERATED
  // dispatches while refs exist, so we derive suggestions directly here.
  const effectiveSuggestions = useMemo(() => {
    if (suggestions) return suggestions
    const suggestionContext = context?.[0] ?? currentReferences?.[0] ?? currentRepository
    if (suggestionContext) {
      return getThreadStaticSuggestions(suggestionContext)
    }
    return null
  }, [suggestions, context, currentReferences, currentRepository])

  const suggestionReferenceGroup = effectiveSuggestions?.referenceType ?? 'default'

  // Clear suggestions on unmount
  useEffect(() => {
    return () => {
      manager.clearSuggestions()
    }
  }, [manager])

  const createIssueCommand = useMemo<AvailableCommand | undefined>(
    () => availableCommands.find(c => c.key === CREATE_ISSUE_KEY),
    [availableCommands],
  )

  const shouldShowCreateIssue = useMemo(() => {
    const type = effectiveSuggestions?.referenceType
    return type === 'repository' || type === 'issue' || type === 'file' || type === 'file-v2'
  }, [effectiveSuggestions?.referenceType])

  const handleCreateIssueSelect = useCallback(() => {
    if (!createIssueCommand) return

    sendEvent('dotcom_chat.activate', {
      target: 'IMMERSIVE_CREATE_ISSUE_ICEBREAKER_SELECTED',
      mode: 'immersive',
      command: createIssueCommand.key,
      referenceGroup: effectiveSuggestions?.referenceType ?? 'default',
    })

    // Ensure command suggestions render, matching CommandStartersAndSuggestions.
    onChatCommandChange?.(createIssueCommand.key)

    chatInputRef.current?.setText(text => {
      // Replace any existing slash command with /create-issue, matching the command starter behavior.
      const foundCommand = extractChatCommandType(text, availableCommandKeys)

      if (foundCommand) {
        return text.replace(`/${foundCommand}`, `/${createIssueCommand.key}`)
      }

      // Set only the slash command — any previous suggestion pill text would prevent
      // CommandSuggestions from showing (its filter requires textarea text to be a
      // substring of the full suggestion).
      return `/${createIssueCommand.key}`
    })

    textAreaRef.current?.focus()
  }, [
    availableCommandKeys,
    chatInputRef,
    createIssueCommand,
    onChatCommandChange,
    effectiveSuggestions?.referenceType,
    textAreaRef,
  ])

  const handleSelect = useCallback(
    (suggestion: GeneratedSuggestion) => {
      const content = (suggestion.prompt ?? suggestion.question).trim()

      sendEvent('copilot_generated_suggestion_click', {
        intent: suggestion.intent,
        mode: suggestion.mode,
        contextType: context?.[0]?.type,
        content: suggestion.question,
        referenceGroup: suggestionReferenceGroup,
        suggestionPillsEnabled: copilotFeatureFlags.copilotImmersiveSuggestionPills,
      })

      chatInputRef.current?.setText(content)
      textAreaRef.current?.focus()
    },
    [suggestionReferenceGroup, context, chatInputRef, textAreaRef],
  )

  const iconForSuggestion = useCallback((iconKey?: string) => {
    switch (iconKey) {
      case 'ask':
        return QuestionIcon
      case 'overview':
        return NoteIcon
      case 'getting-started':
        return RocketIcon
      case 'summarize':
        return BookIcon
      case 'explain':
        return InfoIcon
      case 'improve':
        return ToolsIcon
      case 'performance':
        return TelescopeIcon
      case 'commits':
        return GitCommitIcon
      case 'changes':
        return FileDiffIcon
      case 'review':
        return ChecklistIcon
      case 'feedback':
        return CommentDiscussionIcon
      case 'next-steps':
        return LightBulbIcon
      case 'create-pr':
        return GitPullRequestIcon
      case 'logs':
        return LogIcon
      case 'security':
        return ShieldLockIcon
      case 'filter':
        return HistoryIcon
      case 'search':
        return SearchIcon
      default:
        return CommentDiscussionIcon
    }
  }, [])

  // Track when suggestions are shown
  useEffect(() => {
    if (!effectiveSuggestions?.suggestions?.length) {
      shownSuggestionsKeyRef.current = null
      return
    }
    if (shownSuggestionsKeyRef.current === suggestionReferenceGroup) return
    shownSuggestionsKeyRef.current = suggestionReferenceGroup

    sendEvent('copilot_generated_suggestions_shown', {
      referenceGroup: suggestionReferenceGroup,
    })
  }, [effectiveSuggestions, suggestionReferenceGroup])

  const hasSuggestions = !!effectiveSuggestions?.suggestions?.length
  if (!hasSuggestions && !(shouldShowCreateIssue && createIssueCommand)) return null

  const commandSuggestions = chatCommand
    ? availableCommands.find(command => command.key === chatCommand)?.suggestions
    : null
  const hasCommandSuggestions = commandSuggestions && commandSuggestions.length > 0

  const handleCommandSuggestionSelect = (command: CopilotChatCommandType, suggestion: string) => {
    sendEvent('dotcom_chat.activate', {
      target: 'COMMAND_SUGGESTION_SELECTED',
      mode: 'immersive',
      command,
      suggestion,
    })
    chatInputRef.current?.setText(`/${command} ${suggestion}`.trim())
    textAreaRef.current?.focus()
  }

  return (
    <div className={styles.container}>
      {chatCommand && hasCommandSuggestions && containerRef && (
        <CommandSuggestions
          key={chatCommand}
          chatCommand={chatCommand}
          suggestions={commandSuggestions}
          onSelect={handleCommandSuggestionSelect}
          anchorRef={containerRef}
          textAreaRef={textAreaRef}
        />
      )}
      <ul className={styles.list} aria-label="Suggested prompts">
        {shouldShowCreateIssue && createIssueCommand && (
          <li>
            <CommandPill icon={createIssueCommand.icon} onClick={handleCreateIssueSelect}>
              {createIssueCommand.name}
            </CommandPill>
          </li>
        )}

        {effectiveSuggestions?.suggestions?.map(s => (
          <li key={s.question}>
            <CommandPill icon={iconForSuggestion(s.iconKey)} onClick={() => handleSelect(s)}>
              {s.title ?? s.question}
            </CommandPill>
          </li>
        ))}
      </ul>
    </div>
  )
}
